import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua)) return true;
  return window.matchMedia?.("(pointer: coarse)").matches && window.innerWidth < 900;
};

const sanitizeAmount = (v) => {
  const n = parseFloat(String(v).replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};

const buildUpiUri = (receiverUpi, receiverName, amt, customerName, refId) => {
  const pn = encodeURIComponent(receiverName || "BINAREX AND CO");
  // Inject the merchant reference id into the transaction note — this lands
  // in the customer's bank statement AND in any aggregator webhook payload,
  // so auto-confirm can match by exact ref-id instead of just amount.
  const noteParts = [refId, `from ${customerName || "Customer"}`].filter(Boolean);
  const tn = encodeURIComponent(noteParts.join(" — "));
  return `upi://pay?pa=${encodeURIComponent(receiverUpi)}&pn=${pn}&am=${amt}&cu=INR&tn=${tn}`;
};

export default function UPIPay() {
  const [isMobile, setIsMobile] = useState(true);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [resolvedUri, setResolvedUri] = useState(null);
  const [resolvedReceiver, setResolvedReceiver] = useState(null);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileDevice());
    // Just probe whether UPI is open for business. The actual receiver is
    // resolved server-side at submit time based on the configured strategy
    // (amount range / round-robin / weighted).
    api.get("/upi/status")
      .then((r) => setAvailable(!!r.data?.enabled))
      .catch(() => setAvailable(false))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (!/^[0-9+\-\s()]{7,20}$/.test(mobile.trim())) { toast.error("Please enter a valid mobile number"); return; }
    const amt = sanitizeAmount(amount);
    if (!amt || amt <= 0) { toast.error("Please enter a valid amount"); return; }
    setBusy(true);
    try {
      // Server resolves the receiver based on the configured strategy.
      const { data } = await api.post("/upi/log", {
        customer_name: name.trim(),
        customer_mobile: mobile.trim(),
        amount: amt,
      });
      const uri = buildUpiUri(data.receiver_upi_id, data.receiver_name, amt, name.trim(), data.merchant_reference_id);
      setResolvedUri(uri);
      setResolvedReceiver({ upi_id: data.receiver_upi_id, receiver_name: data.receiver_name, ref_id: data.merchant_reference_id });

      // iOS Safari UX recovery — if no UPI app is installed, the navigation
      // never happens and the page stays visible. We watch for that and
      // auto-reveal the QR so the customer can scan from another device.
      let intentLanded = false;
      const onBlur = () => { intentLanded = true; };
      const onVisibility = () => { if (document.hidden) intentLanded = true; };
      window.addEventListener("blur", onBlur, { once: true });
      document.addEventListener("visibilitychange", onVisibility, { once: true });

      window.location.href = uri;

      setTimeout(() => {
        window.removeEventListener("blur", onBlur);
        document.removeEventListener("visibilitychange", onVisibility);
        if (!intentLanded && !document.hidden) {
          setShowQR(true);
          toast("No UPI app responded — scan the QR with another phone", { icon: "📱" });
        }
        setBusy(false);
      }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to initiate payment");
      setBusy(false);
    }
  };

  if (!isMobile) {
    return (
      <div className="min-h-screen bg-[#05050A] text-slate-200 flex items-center justify-center px-5">
        <div className="max-w-md w-full bx-card p-8 text-center" data-testid="upi-desktop-blocker">
          <Lucide.Smartphone size={42} className="mx-auto text-purple-300 mb-3" />
          <h1 className="bx-heading text-2xl font-extrabold">Open on your mobile</h1>
          <p className="text-slate-400 text-sm mt-3">
            This UPI payment link is only available on mobile devices.
          </p>
          <p className="text-slate-500 text-xs mt-4">
            Scan the QR or open <span className="bx-mono text-purple-300">binarex.co/UPI</span> from your phone to pay any installed UPI app — GPay, PhonePe, Paytm, BHIM and more.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05050A] text-slate-100" data-testid="upi-mobile-page">
      <Seo title="Pay via UPI — BINAREX.CO" description="Secure UPI payment for BINAREX.CO services." />
      <div
        className="px-5 pt-8 pb-6 relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, rgba(124,58,237,0.25) 0%, transparent 100%)" }}
      >
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 mb-5">
          <div className="bx-heading text-xl font-extrabold">BINAREX.CO</div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Pay via UPI</span>
        </motion.div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Lucide.ShieldCheck size={13} className="text-emerald-300" />
          Secured payment · powered by your UPI app
        </div>
      </div>

      <div className="px-5 max-w-md mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bx-card p-5">
          {loading ? (
            <div className="text-center py-8 text-slate-400 text-sm flex items-center justify-center gap-2"><Lucide.Loader2 className="animate-spin" size={14} /> Loading…</div>
          ) : !available ? (
            <div className="text-center py-8" data-testid="upi-no-receiver">
              <Lucide.AlertTriangle size={26} className="mx-auto text-amber-300" />
              <p className="mt-3 text-sm text-slate-300">UPI payments are temporarily unavailable.</p>
              <p className="mt-1 text-xs text-slate-500">Please contact support.</p>
            </div>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Paying</div>
              <div className="flex items-center justify-between mt-1.5">
                <div>
                  <div className="text-sm font-semibold">BINAREX AND CO</div>
                  <div className="text-xs text-slate-500 mt-0.5">Receiver UPI ID will be assigned on submit</div>
                </div>
                <Lucide.BadgeCheck size={22} className="text-emerald-300" />
              </div>

              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="upi-form">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400">Your name *</label>
                  <input
                    data-testid="upi-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    autoComplete="name"
                    required
                    maxLength={120}
                    className="bx-input mt-1 w-full"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400">Mobile number *</label>
                  <input
                    data-testid="upi-mobile"
                    type="tel"
                    inputMode="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="10-digit mobile"
                    autoComplete="tel"
                    required
                    maxLength={20}
                    className="bx-input mt-1 w-full"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400">Amount (INR) *</label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                    <input
                      data-testid="upi-amount"
                      type="number"
                      inputMode="decimal"
                      min="1"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      required
                      className="bx-input w-full pl-8"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="upi-pay-now"
                  disabled={busy}
                  className="w-full mt-2 rounded-xl py-3.5 font-bold text-base disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{
                    background: "linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)",
                    boxShadow: "0 12px 30px -10px rgba(139,92,246,0.6)",
                  }}
                >
                  {busy ? <Lucide.Loader2 className="animate-spin" size={18} /> : <Lucide.IndianRupee size={18} />}
                  PAY NOW
                </button>

                {resolvedReceiver && (
                  <div className="mt-3 text-center text-[11px] text-slate-400" data-testid="upi-resolved-receiver">
                    Paying to <span className="bx-mono text-purple-300">{resolvedReceiver.upi_id}</span>
                    {resolvedReceiver.ref_id && (
                      <div className="mt-1 text-[10px] text-slate-500">
                        Ref: <span className="bx-mono text-purple-300/80">{resolvedReceiver.ref_id}</span>
                      </div>
                    )}
                  </div>
                )}

                {resolvedReceiver?.ref_id && (
                  <a
                    href={`/upi/thanks/${resolvedReceiver.ref_id}`}
                    className="block text-center mt-3 rounded-xl py-2.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-purple-300 border border-purple-400/20"
                    data-testid="upi-check-status"
                  >
                    <Lucide.Search size={12} className="inline mr-1" /> After paying, tap here to check status
                  </a>
                )}

                {showQR && resolvedUri && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="rounded-xl bg-white p-4 mx-auto"
                    style={{ width: 250 }}
                    data-testid="upi-qr"
                  >
                    <QRCodeCanvas value={resolvedUri} size={218} level="M" includeMargin={false} />
                  </motion.div>
                )}

                {resolvedUri && (
                  <a
                    href={resolvedUri}
                    className="block text-center text-xs text-purple-300 underline mt-2"
                    data-testid="upi-reopen-intent"
                  >
                    Didn't open? Tap to retry payment app
                  </a>
                )}
              </form>

              <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-around text-[10px] text-slate-500">
                <span>GPay</span>
                <span>·</span>
                <span>PhonePe</span>
                <span>·</span>
                <span>Paytm</span>
                <span>·</span>
                <span>BHIM</span>
                <span>·</span>
                <span>Any UPI</span>
              </div>
            </>
          )}
        </motion.div>

        <p className="text-[10px] text-slate-500 text-center mt-4">
          BINAREX AND CO · Direct UPI payment · No card details collected
        </p>
      </div>
    </div>
  );
}
